import { Environment, RecordSource, Store } from 'relay-runtime';
import {
  type Middleware,
  type MiddlewareRaw,
  type MiddlewareSync,
  RelayNetworkLayer,
  authMiddleware,
  errorMiddleware,
  retryMiddleware,
  uploadMiddleware,
  urlMiddleware,
} from 'react-relay-network-modern';

import { getKeycloakClient } from './keycloak.js';

const GRAPHQL_URL = '/api/graphql';

let anonymousEnvironment: Environment | undefined;
let environment: Environment | undefined;

/**
 * Get organization slug from current window location
 */
export function getOrganizationSlug(): string | null {
  const { pathname } = window.location;
  const slug = pathname.split('/')[1];
  if (slug == null) {
    return null;
  }

  return slug;
}

/**
 * Get access token
 */
// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
export async function getAccessToken(): Promise<string | void | undefined> {
  const keycloak = await getKeycloakClient();

  return keycloak
    .updateToken(100)
    .then(() => {
      return keycloak.token;
    })
    .catch(async () => {
      return keycloak.login();
    });
}

/**
 * Get network
 */
export function getNetwork(enableAuth: boolean): RelayNetworkLayer {
  const middlewares: (Middleware | MiddlewareSync | MiddlewareRaw)[] = [
    uploadMiddleware(),
    urlMiddleware({
      url: GRAPHQL_URL,
      // This is a trick to pass the organization's slug in the headers to the backend.
      // Otherwise, it's impossible for the backend to guess it
      // @ts-expect-error: OK
      headers() {
        const slug = getOrganizationSlug();
        if (slug == null) {
          return {};
        }

        return {
          organizationSlug: slug,
        };
      },
    }),
    retryMiddleware({
      fetchTimeout: 600000, // 600000 miliseconds, 10 minutes
    }),
  ];

  if (enableAuth) {
    middlewares.push(
      authMiddleware({
        token: async () => {
          return getAccessToken().then((result) => {
            if (result == null) {
              return '';
            }

            return result;
          });
        },
      }),
    );
  }

  if (__DEV__) {
    middlewares.push(errorMiddleware());
  }

  return new RelayNetworkLayer(middlewares);
}
/**
 * Get anonymous relay environment
 */
export function getAnonymousEnvironment(): Environment {
  anonymousEnvironment ??= new Environment({
    network: getNetwork(false),
    store: new Store(new RecordSource()),
  });

  return anonymousEnvironment;
}

/**
 * Get anonymous relay environment
 */
export function getEnvironment(): Environment {
  environment ??= new Environment({
    network: getNetwork(true),
    store: new Store(new RecordSource()),
  });

  return environment;
}
